// AppSidebar.js
// Permanent retrátil sidebar — replaces TopBar navigation.
// Desktop: icon-only (64px) or expanded (240px), permanent Drawer.
// Mobile: overlay Drawer, opens via hamburger in TopActionBar.
//
// Collapse state persisted in localStorage key 'progerar.sidebar.collapsed'.
// Default: mobile=closed, desktop >1280px=expanded, desktop narrow=icon-only.
//
// Logout: SEMPRE via <LogoutFlow> (utils/localReset/performLogout) — não reimplementar aqui.

import React, { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { withRouter } from '../../utils/withRouterCompat'
import { connect } from 'react-redux'
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Loading from '../Loading'
import HomeIcon from '@mui/icons-material/Home'
import BarChartIcon from '@mui/icons-material/BarChart'
import SchoolIcon from '@mui/icons-material/School'
import PeopleIcon from '@mui/icons-material/People'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ScienceIcon from '@mui/icons-material/Science'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate'
import LogoutIcon from '@mui/icons-material/Logout'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import MenuIcon from '@mui/icons-material/Menu'

import LogoZoetis from '../../assets/img/logo_corte.png'
import LogoZoetisSecondary from '../../assets/img/logozoetis_menu.png'
import { version } from '../../version'
import Prompt from '../Prompt'
import VersionNotice from './VersionNotice'
import { logout, getLoggedUser } from '../../redux/auth/actions'
import { LocalStorageHelper } from '../../utils/localStorageHelper'
import clearCache from '../../utils/clearCache'
import hasInternet from '../../utils/recognizeInternetConnection'
import { utils } from '../../database'
import { recordEvent } from '../../observability/events'
import LogoutFlow from '../localReset/LogoutFlow'
import CleanupFlow from '../localReset/CleanupFlow'
import {
  getCleanupButtonState,
  getSidebarSyncActionState,
} from '../../utils/localReset/cleanupButton'

const SIDEBAR_WIDTH_EXPANDED = 240
const SIDEBAR_WIDTH_COLLAPSED = 64
const LS_KEY = 'progerar.sidebar.collapsed'

// Determina o estado inicial do collapse baseado no viewport.
function getInitialCollapsed(isWide) {
  try {
    const stored = localStorage.getItem(LS_KEY)
    if (stored !== null) return stored === 'true'
  } catch {
    // ignore
  }
  // Default: wide desktop = expanded (false), narrow = collapsed (true)
  return !isWide
}

function AppSidebar(props) {
  const { mobileOpen, onMobileClose, currentSync, loggedUser } = props

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isWide = useMediaQuery('(min-width:1280px)')

  const [collapsed, setCollapsed] = useState(() => getInitialCollapsed(isWide))
  const [logoutModal, setLogoutModal] = useState(false)
  const [logoutFlow, setLogoutFlow] = useState(false)
  const [cleanupFlow, setCleanupFlow] = useState(false)
  const [semenModal, setSemenModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [notesVisible, setNotesVisible] = useState(true)

  const navigate = useNavigate()
  const location = useLocation()

  // Persist collapse state
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, String(collapsed))
    } catch {
      // ignore
    }
  }, [collapsed])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => !c)
  }, [])

  const isActive = useCallback(
    (route) => {
      if (!route) return false
      if (route === '/') return location.pathname === '/'
      return location.pathname.startsWith(route)
    },
    [location.pathname],
  )

  const isOnIATFRoute =
    location.pathname.includes('IATF') || location.pathname.includes('iatf')

  const canUsers =
    currentSync === 2 &&
    hasInternet() &&
    loggedUser?.roles?.[0] !== 'Cliente (somente visualização)'

  const isAdmin = loggedUser?.roles?.includes('Administrador')

  // ─── Nav sections ────────────────────────────────────────────────────────
  // Agrupamento lógico (2026-05-15): Dia a dia / Análise / Gestão.
  // "Meu Perfil" foi movido para bottomItems (convenção UX: conta + logout no rodapé).
  const navSections = [
    {
      key: 'daily',
      label: 'Dia a dia',
      items: [
        {
          key: 'home',
          label: 'Home',
          icon: <HomeIcon />,
          route: '/',
          show: true,
          disabled: false,
          onClick: () => {
            navigate('/')
            if (!isDesktop) onMobileClose()
          },
        },
        {
          key: 'protocols',
          label: 'Protocolos',
          icon: <AssignmentIcon />,
          route: '/protocols',
          show: true,
          disabled: false,
          onClick: () => {
            navigate('/protocols')
            if (!isDesktop) onMobileClose()
          },
        },
        {
          key: 'semen',
          label: 'Sêmen',
          icon: <ScienceIcon />,
          route: null,
          show: !isOnIATFRoute,
          disabled: false,
          onClick: () => {
            if (loggedUser?.isNewPartida) {
              navigate('/bulls/create-for-all/batch/none/management/none')
              if (!isDesktop) onMobileClose()
            } else {
              setSemenModal(true)
            }
          },
        },
      ],
    },
    {
      key: 'analysis',
      label: 'Análise',
      items: [
        {
          key: 'dashboard',
          label: 'Dashboard Geral',
          icon: <BarChartIcon />,
          route: '/dashboard-geral',
          show: true,
          disabled: false,
          onClick: () => {
            navigate('/dashboard-geral')
            if (!isDesktop) onMobileClose()
          },
        },
        {
          key: 'trainings',
          label: 'Treinamentos',
          icon: <SchoolIcon />,
          route: '/treinamentos',
          show: true,
          disabled: false,
          onClick: () => {
            navigate('/treinamentos')
            if (!isDesktop) onMobileClose()
          },
        },
      ],
    },
    {
      key: 'management',
      label: 'Gestão',
      items: [
        {
          key: 'users',
          label: 'Usuários',
          icon: <PeopleIcon />,
          route: '/users',
          show: true,
          disabled: !canUsers,
          onClick: () => {
            if (!canUsers) return
            navigate('/users')
            if (!isDesktop) onMobileClose()
          },
        },
        {
          key: 'admin',
          label: 'Admin',
          icon: <AdminPanelSettingsIcon />,
          route: '/lancamentos-do-administrador',
          show: !!isAdmin,
          disabled: false,
          onClick: () => {
            navigate('/lancamentos-do-administrador')
            if (!isDesktop) onMobileClose()
          },
        },
      ],
    },
  ]

  // ─── Bottom items ─────────────────────────────────────────────────────────
  const online = hasInternet()
  const cleanupState = getCleanupButtonState({ currentSync, online })
  // Sync parado (sucesso OU erro) + online — QA HML F7: com um recusado o ciclo termina em erro
  // para sempre; o gate da saída/limpeza é que protege os dados.
  const syncActionState = getSidebarSyncActionState({ currentSync, online })
  const bottomItems = [
    {
      key: 'profile',
      label: 'Meu Perfil',
      icon: <AccountCircleIcon />,
      route: '/perfil',
      disabled: false,
      onClick: () => {
        navigate('/perfil')
        if (!isDesktop) onMobileClose()
      },
    },
    {
      key: 'update',
      label: 'Atualizar versão',
      icon: <SystemUpdateIcon />,
      disabled: syncActionState.disabled,
      tooltip: syncActionState.tooltip,
      onClick: () => {
        if (syncActionState.disabled) return
        setLoading(true)
        LocalStorageHelper.add('updateVersion', true)
        void recordEvent({
          level: 'info',
          category: 'outro',
          action: 'version_update_started',
          payload: { currentVersion: version },
        })
        clearCache()
      },
    },
    {
      key: 'cleanup',
      label: 'Limpar e recarregar',
      icon: <CleaningServicesIcon />,
      disabled: cleanupState.disabled,
      tooltip: cleanupState.tooltip,
      onClick: () => {
        if (cleanupState.disabled) return
        setCleanupFlow(true)
      },
    },
    {
      key: 'logout',
      label: 'Sair',
      icon: <LogoutIcon />,
      disabled: syncActionState.disabled,
      tooltip: syncActionState.tooltip,
      onClick: () => {
        if (syncActionState.disabled) return
        recordEvent({ level: 'info', category: 'auth', action: 'logout' })
        setLogoutModal(true)
      },
    },
  ]

  // ─── Drawer content ───────────────────────────────────────────────────────
  const showCollapsed = collapsed && isDesktop
  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: showCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
        overflow: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      {/* ── Logo row ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: showCollapsed ? 1 : 2,
          py: 1.5,
          minHeight: 60,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {showCollapsed ? (
          <img
            src={LogoZoetisSecondary}
            alt='Zoetis'
            style={{ height: '36px', width: 'auto' }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <img
              src={LogoZoetisSecondary}
              alt='Zoetis'
              style={{ height: '32px', width: 'auto' }}
            />
            <Box
              aria-hidden
              sx={{
                width: '1px',
                height: '24px',
                bgcolor: 'divider',
              }}
            />
            <img
              src={LogoZoetis}
              alt='ProGERAR'
              style={{ height: '28px', width: 'auto', cursor: 'pointer' }}
              onClick={() => {
                navigate('/')
                if (!isDesktop) onMobileClose()
              }}
            />
          </Box>
        )}
      </Box>

      {/* ── Toggle button (acima do primeiro nav item) ──
          Mesma linha pros dois estados — ícone troca conforme expandido/colapsado.
          Mobile não exibe (drawer fecha via overlay/swipe). */}
      {isDesktop && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: showCollapsed ? 'center' : 'flex-end',
            py: 0.5,
            px: showCollapsed ? 0 : 1,
          }}
        >
          <IconButton
            size='small'
            onClick={toggleCollapsed}
            aria-label={showCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {showCollapsed ? (
              <MenuIcon fontSize='small' />
            ) : (
              <ChevronLeftIcon fontSize='small' />
            )}
          </IconButton>
        </Box>
      )}

      {/* ── Nav sections ── */}
      <List sx={{ flex: 1, pt: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {navSections.map((section, sectionIdx) => {
          const visibleItems = section.items.filter((item) => item.show)
          if (visibleItems.length === 0) return null

          return (
            <React.Fragment key={section.key}>
              {showCollapsed ? (
                sectionIdx > 0 && <Divider sx={{ my: 0.75, mx: 1.5 }} />
              ) : (
                <Typography
                  variant='overline'
                  sx={{
                    display: 'block',
                    px: 2,
                    pt: sectionIdx === 0 ? 0.5 : 1.5,
                    pb: 0.25,
                    color: 'text.secondary',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    lineHeight: 1.5,
                  }}
                >
                  {section.label}
                </Typography>
              )}
              {visibleItems.map((item) => {
                const active = isActive(item.route)
                const button = (
                  <ListItemButton
                    key={item.key}
                    onClick={item.onClick}
                    disabled={item.disabled}
                    selected={active}
                    sx={{
                      minHeight: 48,
                      justifyContent: showCollapsed ? 'center' : 'initial',
                      px: showCollapsed ? 0 : 2,
                      borderRadius: 1,
                      mx: 0.5,
                      mb: 0.25,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        '& .MuiListItemIcon-root': { color: 'white' },
                        '&:hover': { bgcolor: 'primary.dark' },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: showCollapsed ? 0 : 2,
                        justifyContent: 'center',
                        color: item.disabled
                          ? 'text.disabled'
                          : active
                            ? 'inherit'
                            : 'text.secondary',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!showCollapsed && (
                      <ListItemText
                        primary={item.label}
                        slotProps={{
                          primary: {
                            fontSize: '0.9rem',
                            fontWeight: active ? 600 : 400,
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                )

                if (showCollapsed) {
                  return (
                    <Tooltip
                      key={item.key}
                      title={item.label}
                      placement='right'
                      arrow
                    >
                      <span>{button}</span>
                    </Tooltip>
                  )
                }
                return button
              })}
            </React.Fragment>
          )
        })}
      </List>

      <Divider />

      {/* ── Bottom items ── */}
      <List sx={{ pb: 1 }}>
        {bottomItems.map((item) => {
          const active = item.route ? isActive(item.route) : false
          const button = (
            <ListItemButton
              key={item.key}
              onClick={item.onClick}
              disabled={item.disabled}
              selected={active}
              sx={{
                minHeight: 48,
                justifyContent: showCollapsed ? 'center' : 'initial',
                px: showCollapsed ? 0 : 2,
                borderRadius: 1,
                mx: 0.5,
                mb: 0.25,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  '& .MuiListItemIcon-root': { color: 'white' },
                  '&:hover': { bgcolor: 'primary.dark' },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: showCollapsed ? 0 : 2,
                  justifyContent: 'center',
                  color: item.disabled
                    ? 'text.disabled'
                    : active
                      ? 'inherit'
                      : 'text.secondary',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!showCollapsed && (
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      fontSize: '0.9rem',
                      fontWeight: active ? 600 : 400,
                    },
                  }}
                />
              )}
            </ListItemButton>
          )

          if (showCollapsed || item.tooltip) {
            return (
              <Tooltip
                key={item.key}
                title={
                  item.tooltip ? `${item.label}: ${item.tooltip}` : item.label
                }
                placement='right'
                arrow
              >
                <span>{button}</span>
              </Tooltip>
            )
          }
          return button
        })}
      </List>

      {/* ── App version (último elemento, abaixo do Sair) ── */}
      {!showCollapsed && (
        <Box
          sx={{
            px: 2,
            pb: 1.5,
            pt: 0.5,
            textAlign: 'center',
            color: 'text.disabled',
            fontSize: '0.7rem',
            letterSpacing: '0.03em',
            userSelect: 'none',
            lineHeight: 1.4,
          }}
        >
          {`Versão: ${version}`}
        </Box>
      )}
    </Box>
  )

  // ─── Prompts (copiados de TopBar.js) ──────────────────────────────────────

  const logoutPrompt = (
    <Prompt
      visible={logoutModal}
      title='Sair do ProGERAR?'
      compact
      message={
        <Typography sx={{ lineHeight: 1.5 }}>
          Antes de sair, o ProGERAR envia ao servidor o que ainda não foi
          enviado e salva um arquivo de backup neste aparelho. Isso pode levar
          alguns minutos: mantenha esta aba aberta e com internet.
        </Typography>
      }
      buttons={[
        { label: 'Cancelar', onClick: () => setLogoutModal(false) },
        {
          label: 'Sair',
          variant: 'contained',
          onClick: () => {
            setLogoutModal(false)
            setLogoutFlow(true)
          },
        },
      ]}
    />
  )

  const semenPrompt = (
    <Prompt
      visible={semenModal && !loggedUser?.isNewPartida}
      title='Atenção!'
      message='Tivemos alterações importantes na estrutura dos dados de Touro e Partida. Para evitar problemas na sincronização dos seus dados é necessário retornar a tela de login e entrar com seu usuário e senha novamente. Caso tenha dúvidas ou problemas, entre em contato com nossa equipe de atendimento, estaremos à disposição.'
      buttons={[
        {
          label: 'Fechar',
          onClick: () => setSemenModal(false),
        },
        {
          label: 'Ir para o Login',
          disabled: syncActionState.disabled,
          onClick: () => {
            setSemenModal(false)
            setLogoutFlow(true)
          },
        },
      ]}
    />
  )

  const versionNotice = (
    <VersionNotice
      open={
        !!LocalStorageHelper.get('updateVersion') && notesVisible && !loading
      }
      version={version}
      onClose={() => setNotesVisible(false)}
    />
  )

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* Desktop: permanent drawer */}
      {isDesktop && (
        <Drawer
          variant='permanent'
          open
          sx={{
            width: showCollapsed
              ? SIDEBAR_WIDTH_COLLAPSED
              : SIDEBAR_WIDTH_EXPANDED,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: showCollapsed
                ? SIDEBAR_WIDTH_COLLAPSED
                : SIDEBAR_WIDTH_EXPANDED,
              boxSizing: 'border-box',
              overflow: 'hidden',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
            '@media print': { display: 'none' },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile: temporary overlay drawer */}
      {!isDesktop && (
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH_EXPANDED,
              boxSizing: 'border-box',
            },
            '@media print': { display: 'none' },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Loading overlay (atualizar versão) */}
      <Loading open={loading} label='Atualizando aplicativo...' />

      {logoutPrompt}
      {semenPrompt}
      {versionNotice}
      {cleanupFlow && <CleanupFlow onClose={() => setCleanupFlow(false)} />}
      {logoutFlow && (
        <LogoutFlow
          onLoggedOut={() => props.logout()}
          onClose={() => setLogoutFlow(false)}
        />
      )}
    </>
  )
}

const mapStateToProps = (state) => ({
  currentSync: state.sync.currentSync,
})

const mapDispatchToProps = (dispatch) => ({
  logout: async () => await dispatch(logout()),
})

const propsFromDatabase = async () => {
  const loggedUser = await getLoggedUser()
  return { loggedUser }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(utils.withPropsFromDatabase(propsFromDatabase, withRouter(AppSidebar)))
